import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@mui/material";

import { useGetShippingZones } from "../../../hooks/shipping/useGetShippingZones";
import { useDeleteShippingZone } from "../../../hooks/shipping/useDeleteShippingZone";
import { useUpdateShippingZone } from "../../../hooks/shipping/useUpdateShippingZone";
import useTableActions from "../../../hooks/common/useTableActions";

import TableLayout from "../../global/Table/TableLayout";
import { ShippingZonesTableColumns } from "./Table/ShippingZonesTableColumns";
import ShippingZonesTableRow from "./Table/ShippingZonesTableRow";
import FormAddEditShippingZone from "./FormAddEditShippingZone";

const ShippingZonesTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const { deleteShippingZoneMutate, isDeleting } = useDeleteShippingZone();
    const { updateShippingZoneMutate } = useUpdateShippingZone();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleMenuDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/shipping",
        deleteSingle: deleteShippingZoneMutate,
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        sort_by: "created_at",
        sort_order: order,
    }), [page, perPage, search, order]);

    const { zones, meta, isLoading, error, refetch } = useGetShippingZones(params);

    const openCreate = () => {
        setEditingItem(null);
        setDialogOpen(true);
    };

    const openEdit = () => {
        if (!selectedItem) return;
        setEditingItem(selectedItem);
        setDialogOpen(true);
        handleMenuClose();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
    };

    const handleToggleActive = (item) => {
        updateShippingZoneMutate({
            id: item.id,
            data: { is_active: !item.is_active },
        });
    };

    const renderRow = (rowParams) => (
        <ShippingZonesTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onToggleActive={handleToggleActive}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "مناطق الشحن" : "Shipping Zones"}
                subtitle={isArabic ? "إدارة المناطق الجغرافية والمواقع المغطاة" : "Manage geographic zones and covered locations"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "إضافة منطقة" : "Add Zone"}
                onAddClick={openCreate}
                isLoading={isLoading}
                error={error}
                items={zones}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={ShippingZonesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={openEdit}
                onMenuDelete={handleMenuDelete}
                isDeletingSingle={isDeleting}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, zones)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                emptyLabelAr="لا توجد مناطق شحن"
                emptyLabelEn="No shipping zones"
                refetch={refetch}
            />

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
                <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
                    <FormAddEditShippingZone
                        isEdit={!!editingItem}
                        currentData={editingItem}
                        onSubmitSuccess={closeDialog}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ShippingZonesTable;
